/**
 * morph.ts — the overview → detail shared-element morph (View Transitions API).
 *
 * DESIGN.md's signature moment: tapping a hospital card grows *that card's*
 * beating heart, name and hero number continuously into the detail hero (and
 * back), so you never lose your place. Implemented as a **progressive
 * enhancement** on top of normal React Router navigation:
 *
 *   - The router stays primary. A plain deep-link or hard-reload onto
 *     `/ps/:slug` renders exactly as before and plays **no** morph — only the
 *     in-app card→detail click (and the back link) morphs.
 *   - No-ops gracefully where `document.startViewTransition` is unsupported
 *     (older Safari/Firefox) and under `prefers-reduced-motion`: the caller
 *     falls through to a normal navigation (see `useMorphNavigate().enabled`).
 *
 * Technique (matches the proven prototype): right before the snapshot, assign a
 * matched `view-transition-name` to the shared elements on the *source* (the
 * clicked card / the hero) and — once the destination is committed — on the
 * *destination*. Only the clicked card and the single hero ever bear a name, so
 * each token stays unique per document. Names are cleared once the transition
 * finishes. Transform/opacity only, ~280ms, on the shipped easing curve (CSS in
 * index.css).
 */

import { useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import { prefersReducedMotion } from "./countUp";

// ── Shared view-transition-name tokens (matched across each pair) ────────────

const VT_NAMES = {
  surface: "pspulse-morph-surface",
  heart: "pspulse-morph-heart",
  name: "pspulse-morph-name",
  number: "pspulse-morph-number",
} as const;

// ── Capability detection ─────────────────────────────────────────────────────

/** Whether the View Transitions API is available in this environment. */
export function supportsViewTransitions(): boolean {
  return typeof document !== "undefined" && typeof document.startViewTransition === "function";
}

// ── Run-state (module singletons) ────────────────────────────────────────────

/**
 * A morph is "in flight" from the synchronous route commit until the transition
 * finishes. The destination reads this at mount (frozen) to (a) suppress its
 * number count-up — the morph *is* the reveal — and (b) skip its one-shot
 * rise-in so the heart doesn't reset after the morph settles.
 */
let morphing = false;
export function isMorphing(): boolean {
  return morphing;
}

/**
 * The values the source card already knows and the destination hero needs at
 * snapshot time, so the big number morphs continuously instead of fading in
 * from an empty/no-data state while its own query is still loading. Deliberately
 * NOT persisted on the URL/history — a plain deep-link or reload has no seed and
 * so renders (and counts up) exactly as before.
 */
export interface MorphSeed {
  total: number;
  nameIt: string;
  nameDe: string;
  measuredAt: string | null;
}

let seed: MorphSeed | null = null;
export function getMorphSeed(): MorphSeed | null {
  return seed;
}

/**
 * The overview's last-known `scrollY`, so navigating back to it — on the morph
 * path OR any plain path — lands the user where they left off ("never lose your
 * place", DESIGN.md). Written continuously by `ScrollManager`'s scroll listener
 * while on the overview, and explicitly by `toDetail` on the way out (before we
 * reset scroll for the forward morph).
 *
 * Persisted to `sessionStorage` under a single `/`-scoped key (issue #104), so
 * the position also survives a **hard reload** — where every module singleton
 * resets, dropping `overviewScrollY` back to 0 and (before #104) dumping the
 * reloader at the top. sessionStorage (not history.state) is deliberate: the
 * scroll target is a single, page-global value with no need to key it per
 * history entry, and history.state is owned by react-router (its `usr`/`key`)
 * so writing there means a read-merge-write dance to avoid clobbering the
 * router — sessionStorage sidesteps that entirely. It is never cleared: the
 * value is only ever *read* when landing back on the overview (where restoring
 * is always what the user wants — "never lose your place"), so a stale entry
 * can't leak anywhere. A genuine forward nav lands on a non-`/` route, whose
 * `scrollToTop` doesn't read it; a fresh visit simply overwrites it.
 *
 * The in-memory `overviewScrollY` is kept as a fast, always-available fallback:
 * the getter must stay synchronous (it's read inside the View-Transition
 * `flushSync`, morph-back path) and must never throw, so if sessionStorage is
 * unavailable (Safari private mode, quota, disabled) we degrade to the
 * in-session module value instead of crashing.
 */
const OVERVIEW_SCROLL_KEY = "pspulse:overviewScrollY";

let overviewScrollY = 0;

/** Read the persisted overview offset. Returns 0 (falls back to module state)
 *  on an absent/invalid entry or any sessionStorage failure — never throws. */
function readPersistedScrollY(): number {
  try {
    const raw = sessionStorage.getItem(OVERVIEW_SCROLL_KEY);
    if (raw === null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Update the in-memory offset ONLY — no I/O. The hot per-scroll path so the
 *  `ScrollManager` listener can update eagerly on every event and debounce the
 *  (comparatively expensive) sessionStorage write separately (#104). */
export function setOverviewScrollY(y: number): void {
  overviewScrollY = y;
}

/** Persist the current in-memory offset to sessionStorage (best-effort). */
export function persistOverviewScrollY(): void {
  try {
    sessionStorage.setItem(OVERVIEW_SCROLL_KEY, String(overviewScrollY));
  } catch {
    /* best-effort; the in-memory value still carries it for this session */
  }
}

/** Bank the offset eagerly AND persist immediately — used where the write must
 *  not be deferred (the in-app forward morph banks the overview position right
 *  before navigating). The listener path debounces via {@link setOverviewScrollY}
 *  + {@link persistOverviewScrollY} instead. */
export function saveOverviewScrollY(y: number): void {
  setOverviewScrollY(y);
  persistOverviewScrollY();
}

export function getSavedOverviewScrollY(): number {
  // Prefer the persisted value (survives reload); fall back to the in-memory
  // one when nothing is persisted (post-`scrollToTop` clear, or sessionStorage
  // unavailable). Both are written together by `saveOverviewScrollY`, so when
  // present they agree — reading sessionStorage first is what fixes reload.
  const persisted = readPersistedScrollY();
  return persisted > 0 ? persisted : overviewScrollY;
}

// ── Scroll application (shared by the morph and the plain ScrollManager path) ──

/**
 * A pending {@link restoreScroll} re-apply loop, so a forward navigation can
 * cancel it (never yank the viewport back mid-forward-nav on a rapid back→forward).
 */
let restoreRaf = 0;
function cancelRestore(): void {
  if (restoreRaf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(restoreRaf);
  restoreRaf = 0;
}

/**
 * Jump to the document top (forward navigation). Cancels any pending restore.
 * `behavior: "instant"` overrides the global `html { scroll-behavior: smooth }`
 * (index.css) — a navigation must land immediately, and for the morph path the
 * "after" snapshot must be captured at the top *now*, not part-way through a
 * smooth-scroll animation (issue #102). Reduced-motion already forces `auto`.
 */
export function scrollToTop(): void {
  cancelRestore();
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

/** Wall-clock backstop for the {@link restoreScroll} loop. Only reached in the
 *  pathological case where the target is permanently unreachable AND the user
 *  never touches the page — harmless idle churn, not a user-facing fight (the
 *  user-scroll guard stops us the instant they interact). */
const RESTORE_BUDGET_MS = 1200;
/** A drop below the last-applied position larger than this means the *user*
 *  scrolled (not a clamp / not late content growth) — stop, don't yank back. */
const USER_SCROLL_EPSILON = 2;

function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/**
 * Restore the window scroll to `targetY`, re-applying each animation frame until
 * the layout settles tall enough to honour it. Content that measures its own
 * size only after first paint — the region-trend chart and the hospital map size
 * via a `ResizeObserver` (visx `ParentSize`) — and, on a hard reload / native
 * Back, the whole page re-fetching its data, leave the document momentarily too
 * short, so the browser clamps the first `scrollTo`. Re-applying lets the restore
 * "catch up" once that late settle lands, so the navigation ends exactly where
 * the user left off (DESIGN.md "never lose your place").
 *
 * Issue #104: the previous **fixed ~10-frame (~160ms) budget** expired *before*
 * that async settle on the native-Back (popstate) path — the page re-fetches and
 * the chart/map re-measure well past 160ms — landing ~1 card (~99px) short. So we
 * keep re-applying until we reach the target. The loop stops on the FIRST of:
 *   1. **Reached the target** — the document grew tall enough (the common path).
 *   2. **The user scrolled away** — if `scrollY` drops below where we last placed
 *      them while the target is still out of reach, they're interacting; stop and
 *      never re-yank them. This is the precise fix for "fighting a user who
 *      scrolls up when the target is unreachable" — a clamp or late content
 *      growth keeps `scrollY == lastApplied` (no false trip), only a genuine user
 *      move drops it. (A `document.scrollHeight`-settled bail was considered and
 *      rejected: a document that sits briefly short then grows — a slow async
 *      data fetch — is indistinguishable from a permanently-short one, so a
 *      settle bail would race and cut off the very late-growth catch-up this
 *      rework exists to fix. The user-scroll guard removes the actual harm
 *      without that regression.)
 *   3. **Wall-clock backstop** — bounds idle churn if the target is permanently
 *      unreachable and the user never interacts. Not a user-facing fight.
 * No-ops the retry when the target is the top.
 */
export function restoreScroll(targetY: number): void {
  cancelRestore();
  // `behavior: "instant"` overrides the global smooth scroll — restoring must
  // land immediately, not glide (issue #102).
  window.scrollTo({ top: targetY, left: 0, behavior: "instant" });
  if (targetY <= 0 || typeof requestAnimationFrame !== "function") return;
  const startedAt = nowMs();
  let lastApplied = window.scrollY; // where the (possibly clamped) scroll landed
  const reapply = (): void => {
    // 1) Reached the target — stop (the common path; document grew tall enough).
    if (Math.abs(window.scrollY - targetY) <= 1) {
      restoreRaf = 0;
      return;
    }
    // 2) User scrolled up from where we last placed them (target still short) →
    //    never yank them back.
    if (lastApplied - window.scrollY > USER_SCROLL_EPSILON) {
      restoreRaf = 0;
      return;
    }
    // 3) Wall-clock backstop.
    if (nowMs() - startedAt >= RESTORE_BUDGET_MS) {
      restoreRaf = 0;
      return;
    }
    window.scrollTo({ top: targetY, left: 0, behavior: "instant" });
    lastApplied = window.scrollY;
    restoreRaf = requestAnimationFrame(reapply);
  };
  restoreRaf = requestAnimationFrame(reapply);
}

/**
 * Monotonic per-run token. Only the LATEST morph run is allowed to reset the
 * shared run-state on completion — see {@link runMorph} for why this matters
 * when a morph is interrupted mid-flight.
 */
let currentMorph = 0;

// ── Tagging ──────────────────────────────────────────────────────────────────

function tagChild(root: HTMLElement, selector: string, name: string): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (el) el.style.viewTransitionName = name;
}

/**
 * Tag a card `<article>` or the detail hero `<header>` (and its heart / name /
 * number descendants) with the matched shared-element names.
 */
export function tagMorphRoot(root: HTMLElement): void {
  root.style.viewTransitionName = VT_NAMES.surface;
  tagChild(root, '[data-morph="heart"]', VT_NAMES.heart);
  tagChild(root, '[data-morph="name"]', VT_NAMES.name);
  tagChild(root, '[data-morph="number"]', VT_NAMES.number);
}

/** Remove every inline `view-transition-name` we assigned, so the next morph starts clean. */
export function clearMorphNames(): void {
  document.querySelectorAll<HTMLElement>('[style*="view-transition-name"]').forEach((el) => {
    el.style.viewTransitionName = "";
  });
}

// ── Core ──────────────────────────────────────────────────────────────────────

/**
 * Run one shared-element morph: tag the source, commit the route change
 * synchronously (so the destination DOM is ready before the "after" snapshot),
 * tag the freshly-committed destination, then clear on completion.
 */
function runMorph(
  commit: () => void,
  sourceEl: HTMLElement | null,
  findDestEl: () => HTMLElement | null,
  nextSeed: MorphSeed | null,
  scrollAfterCommit: () => void,
): void {
  // Belt-and-suspenders — callers gate on `enabled`, but never assume support.
  if (!supportsViewTransitions()) {
    commit();
    scrollAfterCommit();
    return;
  }

  const token = ++currentMorph;
  morphing = true;
  seed = nextSeed;
  if (sourceEl) tagMorphRoot(sourceEl);

  const transition = document.startViewTransition(() => {
    // flushSync forces React to render+commit the navigation synchronously here,
    // so the destination's DOM (and its shared elements) exist before the API
    // captures the "after" snapshot on the next frame.
    flushSync(() => {
      commit();
    });
    // Reset/restore scroll *inside* the update callback — synchronously, before
    // it returns — so the API captures the "after" snapshot at the intended
    // scroll position. Skip this and the shared elements are laid out relative
    // to document-top and morph up-and-off the viewport at any non-zero scrollY
    // (issue #102). This MUST NOT be deferred to a later effect.
    scrollAfterCommit();
    const destEl = findDestEl();
    if (destEl) tagMorphRoot(destEl);
  });

  // `finished` REJECTS whenever the browser skips an in-flight transition — which
  // it does automatically when a second startViewTransition begins while one is
  // still running (a routine impatient tap: card → back before the ~280ms morph
  // settles). Consume BOTH settlements so an interrupt never leaves an unhandled
  // rejection. The `token` guard then ensures only the LATEST run resets the
  // shared run-state: a superseded transition's `finished` settles (as a
  // microtask) *before* the newer run's update callback mounts the destination
  // next frame, so without the guard it would flip `morphing`/`seed` off and
  // strip the freshly-applied names mid-flight — making the returning overview
  // re-run its reveal cascade instead of morphing ("losing your place").
  transition.finished.then(settle, settle);
  function settle(): void {
    if (token !== currentMorph) return;
    clearMorphNames();
    morphing = false;
    seed = null;
  }
}

// ── Hook ────────────────────────────────────────────────────────────────────

interface MorphNavigate {
  /**
   * Whether an in-app navigation should morph. `false` where View Transitions
   * is unsupported OR the user prefers reduced motion — the caller then lets
   * the plain router navigation proceed (no morph, no jank).
   */
  enabled: boolean;
  /** Morph the clicked overview card into the detail hero for `slug`. */
  toDetail: (slug: string, cardEl: HTMLElement | null, seed: MorphSeed) => void;
  /** Morph the detail hero back into its overview card. */
  toOverview: (slug: string) => void;
}

/**
 * React hook exposing the two morphing navigations. Use `enabled` to decide
 * whether to intercept a `<Link>` click; when disabled, don't `preventDefault`
 * and let React Router navigate normally.
 */
export function useMorphNavigate(): MorphNavigate {
  const navigate = useNavigate();
  const enabled = supportsViewTransitions() && !prefersReducedMotion();

  return {
    enabled,
    toDetail(slug, cardEl, nextSeed) {
      // Bank the overview's scroll before we leave, so the return trip lands
      // back here — then reset to the top inside the transition.
      saveOverviewScrollY(window.scrollY);
      runMorph(
        () => {
          void navigate(`/ps/${slug}`);
        },
        cardEl,
        () => document.querySelector<HTMLElement>("[data-morph-hero]"),
        nextSeed,
        scrollToTop,
      );
    },
    toOverview(slug) {
      runMorph(
        () => {
          void navigate("/");
        },
        document.querySelector<HTMLElement>("[data-morph-hero]"),
        () => document.querySelector<HTMLElement>(`[data-morph-card="${slug}"]`),
        null,
        () => restoreScroll(getSavedOverviewScrollY()),
      );
    },
  };
}
